import clsx from 'clsx';
import type { FunctionComponent, HTMLAttributes, ReactNode } from 'react';

import { SoftSkillIcon } from '../icons';
import { useCardContext } from './Card';

type SlotProps = HTMLAttributes<HTMLDivElement> & { readonly children: ReactNode };

export const CardHeader: FunctionComponent<SlotProps> = ({ children, className, ...props }) => {
    const { isActive } = useCardContext();

    return (
        <div
            className={clsx(
                'relative flex min-h-[40px] w-full flex-1 gap-s',
                isActive ? 'text-text-on-accent' : 'text-text-content-strong',
                className,
            )}
            {...props}
        >
            {children}
        </div>
    );
};

export const CardTitleGroup: FunctionComponent<SlotProps> = ({ children, className, ...props }) => (
    <div className={clsx('flex min-w-0 flex-1 flex-col', className)} {...props}>
        {children}
    </div>
);

export const CardTitle: FunctionComponent<SlotProps> = ({ children, className, ...props }) => {
    const { isActive, isSelected, surface } = useCardContext();

    return (
        <div
            className={clsx(
                'flex min-h-4xl min-w-0 items-center text-body-s font-medium select-none whitespace-normal break-words tracking-tight',
                surface === 'recessed' && isSelected && !isActive && 'text-text-primary',
                className,
            )}
            data-testid="card-title"
            {...props}
        >
            {children}
        </div>
    );
};

export const CardSubtitle: FunctionComponent<SlotProps> = ({ children, className, ...props }) => {
    const { isActive } = useCardContext();

    return (
        <div
            className={clsx(
                'flex min-h-[26px] min-w-0 items-center text-body-xs font-normal select-none leading-[1.125rem] tracking-tight',
                isActive ? 'text-text-on-accent' : 'text-text-content-subtle',
                className,
            )}
            {...props}
        >
            {children}
        </div>
    );
};

export const CardActions: FunctionComponent<SlotProps & { readonly align?: 'center' | 'start' }> = ({
    align = 'start',
    children,
    className,
    ...props
}) => (
    <div
        className={clsx(
            'relative ml-xl flex shrink-0 items-center gap-m',
            align === 'center' ? 'self-center' : '-mt-xxs self-start',
            className,
        )}
        {...props}
    >
        {children}
    </div>
);

export const CardGeneratingIndicator: FunctionComponent = () => (
    <span
        className="text-indicator-highlighted-icon relative -top-px ml-xs inline-flex align-middle"
        data-testid="generating-indicator"
        style={{ animation: 'cardPulse 1s ease-in-out infinite' }}
    >
        <SoftSkillIcon />
    </span>
);
